import type {
  AddCaseRequestMinutePayload,
  CaseRequestMinute,
  CaseRequestMinutesList,
  CaseRequestMinutesResponse,
  CaseRequestMinuteResponse,
} from '../types/caseRequestMinutes';
import { caseApiPdfRequest, caseApiRequest } from './apiClient';
import type { ApiResult } from './types';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function minuteStrField(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function minuteNumField(raw: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const v = raw[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return 0;
}

function normalizeMinute(raw: unknown, index: number, caseRequestId: string): CaseRequestMinute {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    id: minuteStrField(r, 'id') || `minute-${index}`,
    caseRequestId: minuteStrField(r, 'case_request_id', 'caseRequestId') || caseRequestId,
    comment: minuteStrField(r, 'comment'),
    createdAt: minuteStrField(r, 'created_at', 'createdAt'),
    createdBy: minuteStrField(r, 'created_by', 'createdBy'),
    createdByName: minuteStrField(r, 'created_by_name', 'createdByName'),
    createdByRole: minuteStrField(r, 'created_by_role', 'createdByRole'),
  };
}

function unwrapEnvelopeData(raw: unknown): Record<string, unknown> {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  if (r.data && typeof r.data === 'object' && !Array.isArray(r.data)) {
    return r.data as Record<string, unknown>;
  }
  return r;
}

function normalizeMinutesList(caseRequestId: string, raw: unknown): CaseRequestMinutesList {
  const r = unwrapEnvelopeData(raw);
  const minutesRaw = r.minutes;
  const minutes = Array.isArray(minutesRaw)
    ? minutesRaw.map((m, i) => normalizeMinute(m, i, caseRequestId))
    : [];
  const totalMinutesCount =
    minuteNumField(r, 'total_minutes_count', 'totalMinutesCount') || minutes.length;
  return { totalMinutesCount, minutes };
}

/** CMS-CRM-2 — get case request minutes */
export async function getCaseRequestMinutes(caseRequestId: string): Promise<ApiResult<CaseRequestMinutesList>> {
  const id = caseRequestId.trim();
  if (!UUID_RE.test(id)) {
    return { success: false, message: 'Invalid case request id.' };
  }

  const res = await caseApiRequest<CaseRequestMinutesResponse>(
    `/minutes/${encodeURIComponent(id)}`,
  );
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeMinutesList(id, res.data) };
}

/** CMS-CRM-1 — create case request minutes */
export async function addCaseRequestMinute(
  caseRequestId: string,
  comment: string,
): Promise<ApiResult<CaseRequestMinute>> {
  const id = caseRequestId.trim();
  const text = comment.trim();
  if (!UUID_RE.test(id)) {
    return { success: false, message: 'Invalid case request id.' };
  }
  if (!text) return { success: false, message: 'Comment is required.' };

  const body: AddCaseRequestMinutePayload = { comment: text };

  const res = await caseApiRequest<CaseRequestMinuteResponse>(`/minutes/${encodeURIComponent(id)}`, {
    method: 'POST',
    body,
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeMinute(res.data, 0, id) };
}

/** CMS-CRM-3 — get case minutes PDF */
export async function getCaseRequestMinutesPdf(caseRequestId: string): Promise<ApiResult<Blob>> {
  const id = caseRequestId.trim();
  if (!UUID_RE.test(id)) {
    return { success: false, message: 'Invalid case request id.' };
  }

  const res = await caseApiPdfRequest(`/minutes/pdf/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: res.data };
}
